import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuthStore } from "@/store/authStore";

/** How often to re-read the profile while a tab is left open. */
const POLL_MS = 60_000;

/**
 * Keep the browser's idea of the signed-in role in step with the server's.
 *
 * The role is minted into the access token at sign-in and the token is not
 * reissued when an administrator changes it. The server enforces the new role
 * on the very next request, so a demoted user kept seeing administrative
 * navigation that answered 403, and a promoted one saw nothing new until they
 * happened to reload. Nothing told either of them to.
 *
 * `GET /auth/me/` reports the role the server holds right now, so re-reading it
 * is the whole mechanism. It runs on the events that mean somebody is looking
 * at the page, plus a slow interval for a tab parked on a dashboard.
 *
 * Nothing here redirects. `AdminGuard` already refuses a non-admin on
 * `/admin`, and `usePermissions` reads the same store field, so the guard fires
 * on the next render once the role lands. It was never the redirect that was
 * missing, only the updated role for it to act on. A second redirect here would
 * be a duplicate of the routing rules, kept in step by hand.
 */
export function useRoleSync() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.user?.role);
  const fetchMe = useAuthStore((s) => s.fetchMe);
  const isImpersonating = useAuthStore((s) => s.impersonation.isImpersonating);

  // Compared against, never rendered, so a ref: writing it must not cause the
  // render that reads it.
  const knownRole = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!isAuthenticated) {
      knownRole.current = undefined;
      return;
    }

    const check = () => {
      // A hidden tab is not being read, and its turn comes on visibilitychange.
      if (document.visibilityState === "hidden") return;
      void fetchMe();
    };

    check();
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", check);
    const id = window.setInterval(check, POLL_MS);

    return () => {
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", check);
      window.clearInterval(id);
    };
  }, [isAuthenticated, fetchMe]);

  // Entering or leaving impersonation swaps `user` for somebody else, which is
  // a different person's role rather than a change to your own. Nothing
  // unmounts on the way -- `MainLayout` is a layout route and `admin/*` renders
  // inside it, so `navigate("/")` after `startImpersonation` leaves this hook
  // mounted, and `isAuthenticated` never flips, so the reset above never runs.
  // The ref still held the administrator's own role, so both ends of an
  // impersonation session told them an administrator had changed their role,
  // next to the correct `Now impersonating X` toast. Adopt the new role
  // silently and let the next genuine change be the one that speaks.
  const knownImpersonating = useRef(isImpersonating);

  // Watching the value rather than the fetch keeps this correct whoever
  // refreshed the profile: Settings calls `fetchMe` too, and a change noticed
  // there should read the same as one noticed by the poll.
  useEffect(() => {
    if (!isAuthenticated || !role) return;
    if (knownImpersonating.current !== isImpersonating) {
      knownImpersonating.current = isImpersonating;
      knownRole.current = role;
      return;
    }
    if (knownRole.current === undefined) {
      knownRole.current = role;
      return;
    }
    if (knownRole.current === role) return;

    knownRole.current = role;
    toast.info("Your access has been updated", {
      description:
        "An administrator changed your role. What you can see has changed with it.",
    });
  }, [role, isAuthenticated, isImpersonating]);
}
